import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency, formatRelative } from '@/lib/utils';
import { DEAL_STAGE_COLORS } from '@/lib/constants';
import { DEFAULT_PIPELINE_STAGES } from '@trinco/shared';

// Placeholder for trpc query - will be wired once API routers exist
function useDeals(_filters: { search: string; stage: string }) {
  return { data: null as any, isLoading: false };
}

export function DealsIndexPage() {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  const { data, isLoading } = useDeals({ search, stage: stageFilter });
  const deals = data?.deals ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Deals</h1>
          <p className="text-muted-foreground">View and manage all your deals.</p>
        </div>
        <Link to="/deals/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New deal
          </Button>
        </Link>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={stageFilter} onValueChange={setStageFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stages</SelectItem>
            {DEFAULT_PIPELINE_STAGES.map((stage) => (
              <SelectItem key={stage.slug} value={stage.slug}>
                {stage.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Deal</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Expected Close</TableHead>
              <TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : deals.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No deals found. Create your first deal to get started.
                </TableCell>
              </TableRow>
            ) : (
              deals.map((deal: any) => (
                <TableRow key={deal.id}>
                  <TableCell>
                    <Link
                      to="/deals/$dealId"
                      params={{ dealId: deal.id }}
                      className="font-medium text-primary hover:underline"
                    >
                      {deal.title}
                    </Link>
                  </TableCell>
                  <TableCell>{deal.companyName ?? '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant="secondary"
                      style={{
                        backgroundColor: DEAL_STAGE_COLORS[deal.stage] + '20',
                        color: DEAL_STAGE_COLORS[deal.stage],
                      }}
                    >
                      {deal.stage}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {deal.amount ? formatCurrency(deal.amount, deal.currency ?? 'CAD') : '-'}
                  </TableCell>
                  <TableCell>
                    {deal.expectedClose ? formatRelative(deal.expectedClose) : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatRelative(deal.updatedAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
